import React from "react";
import {
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import RefreshIcon from "@material-ui/icons/Refresh";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import {
  DATE_PRESETS,
  MODULE_OPTIONS,
  ACTION_OPTIONS,
  ROLE_OPTIONS,
  getDateRangeFromPreset,
  MODULE_CONFIGS,
} from "./ProgramLogConstants";

const useStyles = makeStyles((theme) => ({
  filterCard: {
    padding: theme.spacing(2),
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
    },
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      gap: theme.spacing(1),
    },
  },
  controlItem: {
    minWidth: 150,
    flex: "1 1 auto",
    [theme.breakpoints.down("xs")]: {
      minWidth: "100%",
    },
  },
  searchField: {
    minWidth: 220,
    flex: "2 1 auto",
    [theme.breakpoints.down("xs")]: {
      minWidth: "100%",
    },
  },
  actionButtonGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: "auto",
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      width: "100%",
      justifyContent: "space-between",
    },
  },
  customDateRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    flexWrap: "wrap",
    padding: theme.spacing(1),
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
  },
  activeChipsRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: "1px dashed #e2e8f0",
  },
  activeChipLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#64748b",
    marginRight: theme.spacing(0.5),
  },
  chipItem: {
    fontWeight: 500,
    fontSize: "0.75rem",
    borderRadius: 6,
    backgroundColor: "#f1f5f9",
    color: "#1e293b",
    border: "1px solid #cbd5e1",
    height: 26,
  },
  refreshButton: {
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    color: "#475569",
    "&:hover": {
      backgroundColor: "#f8fafc",
      color: "#0f172a",
    },
  },
  resetButton: {
    borderRadius: 8,
    fontSize: "0.75rem",
    textTransform: "none",
    fontWeight: 600,
    color: "#dc2626",
    borderColor: "#fecaca",
    "&:hover": {
      backgroundColor: "#fef2f2",
      borderColor: "#fca5a5",
    },
  },
}));

export default function ProgramLogFilters({
  datePreset,
  setDatePreset,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  filterModule,
  setFilterModule,
  filterAction,
  setFilterAction,
  filterUserId,
  setFilterUserId,
  filterRole,
  setFilterRole,
  referenceInput,
  setReferenceInput,
  users = [],
  loading = false,
  onRefresh,
  onResetFilters,
  setPage,
  isSuperAdmin = false,
}) {
  const classes = useStyles();

  // Never offer "Super Admin" as a pickable Role filter for a non-SuperAdmin
  // viewer - those logs are excluded server-side regardless, so the option
  // would just be a dead end (and reveal that role's logs exist at all).
  const roleOptions = isSuperAdmin
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((opt) => opt.value !== "superAdmin");

  const handleDatePresetChange = (e) => {
    const val = e.target.value;
    setDatePreset(val);
    setPage(0);
    if (val !== DATE_PRESETS.custom) {
      const range = getDateRangeFromPreset(val);
      if (range) {
        setStartDate(range.startDate);
        setEndDate(range.endDate);
      }
    }
  };

  const hasActiveFilters =
    filterModule ||
    filterAction ||
    filterUserId ||
    filterRole ||
    referenceInput.trim() ||
    datePreset !== DATE_PRESETS.last7;

  const selectedUserName = users.find((u) => u._id === filterUserId)?.name;

  return (
    <Paper elevation={0} className={classes.filterCard}>
      <div className={classes.toolbarRow}>
        {/* 1. Date Range Preset */}
        <FormControl
          variant="outlined"
          size="small"
          className={classes.controlItem}
          style={{ minWidth: 140 }}
        >
          <InputLabel id="pl-date-preset-label">Date Range</InputLabel>
          <Select
            labelId="pl-date-preset-label"
            value={datePreset}
            onChange={handleDatePresetChange}
            label="Date Range"
            startAdornment={
              <EventOutlinedIcon
                style={{ fontSize: 18, marginRight: 6, color: "#64748b" }}
              />
            }
          >
            <MenuItem value={DATE_PRESETS.today}>Today</MenuItem>
            <MenuItem value={DATE_PRESETS.yesterday}>Yesterday</MenuItem>
            <MenuItem value={DATE_PRESETS.last7}>Last 7 days</MenuItem>
            <MenuItem value={DATE_PRESETS.thisMonth}>This month</MenuItem>
            <MenuItem value={DATE_PRESETS.lastMonth}>Last month</MenuItem>
            <MenuItem value={DATE_PRESETS.last6Months}>Last 6 months</MenuItem>
            <MenuItem value={DATE_PRESETS.custom}>Custom Range</MenuItem>
          </Select>
        </FormControl>

        {/* 2. Module Selector */}
        <FormControl
          variant="outlined"
          size="small"
          className={classes.controlItem}
          style={{ minWidth: 140 }}
        >
          <InputLabel id="pl-module-label">Module</InputLabel>
          <Select
            labelId="pl-module-label"
            value={filterModule}
            onChange={(e) => {
              setFilterModule(e.target.value);
              setPage(0);
            }}
            label="Module"
          >
            <MenuItem value="">All Modules</MenuItem>
            {MODULE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 3. Activity Selector */}
        <FormControl
          variant="outlined"
          size="small"
          className={classes.controlItem}
          style={{ minWidth: 130 }}
        >
          <InputLabel id="pl-action-label">Activity</InputLabel>
          <Select
            labelId="pl-action-label"
            value={filterAction}
            onChange={(e) => {
              setFilterAction(e.target.value);
              setPage(0);
            }}
            label="Activity"
          >
            <MenuItem value="">All Activities</MenuItem>
            {ACTION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 4. User Selector */}
        <FormControl
          variant="outlined"
          size="small"
          className={classes.controlItem}
          style={{ minWidth: 150 }}
        >
          <InputLabel id="pl-user-label">User</InputLabel>
          <Select
            labelId="pl-user-label"
            value={filterUserId}
            onChange={(e) => {
              setFilterUserId(e.target.value);
              setPage(0);
            }}
            label="User"
          >
            <MenuItem value="">All Users</MenuItem>
            {users.map((u) => (
              <MenuItem key={u._id} value={u._id}>
                {u.name || u.email || u._id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 5. Role Selector */}
        <FormControl
          variant="outlined"
          size="small"
          className={classes.controlItem}
          style={{ minWidth: 110 }}
        >
          <InputLabel id="pl-role-label">Role</InputLabel>
          <Select
            labelId="pl-role-label"
            value={filterRole}
            onChange={(e) => {
              setFilterRole(e.target.value);
              setPage(0);
            }}
            label="Role"
          >
            <MenuItem value="">All Roles</MenuItem>
            {roleOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* 6. Reference Search Field */}
        <TextField
          label="Search Reference / Item #"
          placeholder="e.g. INV-104, ITM-02"
          value={referenceInput}
          onChange={(e) => setReferenceInput(e.target.value)}
          size="small"
          variant="outlined"
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <SearchIcon
                style={{ fontSize: 18, marginRight: 6, color: "#94a3b8" }}
              />
            ),
            endAdornment: referenceInput ? (
              <IconButton
                size="small"
                onClick={() => setReferenceInput("")}
                style={{ padding: 2 }}
              >
                <ClearIcon style={{ fontSize: 16 }} />
              </IconButton>
            ) : null,
          }}
        />

        {/* Action Buttons */}
        <div className={classes.actionButtonGroup}>
          <Tooltip title="Refresh log data">
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={loading}
                className={classes.refreshButton}
                style={{ width: 36, height: 36 }}
              >
                {loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <RefreshIcon style={{ fontSize: 20 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>

          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={onResetFilters}
              startIcon={<RotateLeftIcon style={{ fontSize: 16 }} />}
              className={classes.resetButton}
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Custom Date Range Row */}
      {datePreset === DATE_PRESETS.custom && (
        <div className={classes.customDateRow}>
          <Typography variant="caption" style={{ fontWeight: 600, color: "#475569" }}>
            Custom Date Interval:
          </Typography>
          <TextField
            label="From date"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
            variant="outlined"
            style={{ width: 160 }}
          />
          <TextField
            label="To date"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
            variant="outlined"
            style={{ width: 160 }}
          />
        </div>
      )}

      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <div className={classes.activeChipsRow}>
          <span className={classes.activeChipLabel}>Active Filters:</span>
          {datePreset !== DATE_PRESETS.last7 && (
            <Chip
              size="small"
              label={`Date: ${datePreset}`}
              onDelete={() => {
                setDatePreset(DATE_PRESETS.last7);
                const r = getDateRangeFromPreset(DATE_PRESETS.last7);
                if (r) {
                  setStartDate(r.startDate);
                  setEndDate(r.endDate);
                }
                setPage(0);
              }}
              className={classes.chipItem}
            />
          )}
          {filterModule && (
            <Chip
              size="small"
              label={`Module: ${MODULE_CONFIGS[filterModule]?.label || filterModule}`}
              onDelete={() => {
                setFilterModule("");
                setPage(0);
              }}
              className={classes.chipItem}
            />
          )}
          {filterAction && (
            <Chip
              size="small"
              label={`Action: ${filterAction}`}
              onDelete={() => {
                setFilterAction("");
                setPage(0);
              }}
              className={classes.chipItem}
            />
          )}
          {filterUserId && (
            <Chip
              size="small"
              label={`User: ${selectedUserName || filterUserId}`}
              onDelete={() => {
                setFilterUserId("");
                setPage(0);
              }}
              className={classes.chipItem}
            />
          )}
          {filterRole && (
            <Chip
              size="small"
              label={`Role: ${filterRole}`}
              onDelete={() => {
                setFilterRole("");
                setPage(0);
              }}
              className={classes.chipItem}
            />
          )}
          {referenceInput.trim() && (
            <Chip
              size="small"
              label={`Search: "${referenceInput}"`}
              onDelete={() => {
                setReferenceInput("");
                setPage(0);
              }}
              className={classes.chipItem}
            />
          )}
        </div>
      )}
    </Paper>
  );
}

